import {
  useFirestoreDocument,
  useFirestoreDocumentMutation,
  useFirestoreQuery,
} from '@react-query-firebase/firestore';
import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import { getDocuments } from 'services/api/insights';

import { RoleValue } from '../../types';
import { firestore } from '../Firebase';

const getUsers = () => {
  return collection(firestore, 'usersWeb');
};

export const getUserByUid = (uid: string) => {
  return doc(firestore, 'usersWeb', uid);
};

export const getArtistByProfileLink = async (profileLink: string) => {
  const userRef = query(collection(firestore, 'usersWeb'), where('profileLink', '==', profileLink));

  const user = await getDocs(userRef);

  return user.docs?.[0]
    ? {
        ...user.docs[0].data(),
        id: user.docs[0].id,
      }
    : null;
};

export const useUsersData = (onSuccess: any, onError: any) => {
  return useFirestoreQuery(
    ['users'],
    getUsers(),
    {
      subscribe: true,
    },
    {
      onSuccess,
      onError,
    },
  );
};

export const getUser = (uid: string) => {
  return useFirestoreDocument(
    ['users', uid],
    getUserByUid(uid),
    {
      subscribe: true,
      includeMetadataChanges: true,
    },
    {
      enabled: Boolean(uid),
    },
  );
};

export const updateUser = (id: string) => {
  return useFirestoreDocumentMutation(getUserByUid(id), { merge: true });
};

export const getArtists = (status?: string) => {
  const ref = collection(firestore, 'usersWeb');
  const whereStatements = [where('Role', '==', RoleValue.ARTIST)];

  if (status) whereStatements.push(where('status', '==', status));

  const queryArtists = query(ref, ...whereStatements);
  return useFirestoreQuery(['artists'], queryArtists, {
    subscribe: true,
    includeMetadataChanges: true,
  });
};

export const getMembers = () => {
  const ref = collection(firestore, 'usersWeb');
  const queryMembers = query(ref, where('Role', '!=', RoleValue.ARTIST));
  return useFirestoreQuery(['members'], queryMembers, {
    subscribe: true,
    includeMetadataChanges: true,
  });
};

export const getArtistBalance = async (artistId: string) => {
  const revenueDataSnapshot = await getDocuments(`usersWeb/${artistId}/revenue`);
  const payoutsDataSnapshot = await getDocuments(
    'payouts',
    [
      {
        attribute: 'artistId',
        operator: '==',
        value: artistId,
      },
      {
        attribute: 'status',
        operator: '==',
        value: 'completed',
      },
    ],
    [{ field: 'createdAt', type: 'desc' }],
  );

  const revenueData = revenueDataSnapshot.docs.map((doc) => doc.data());
  const payoutsData = payoutsDataSnapshot.docs.map((doc) => doc.data());

  const artistRevenueSum = revenueData.reduce(
    (previousValue: number, currentValue) => previousValue + currentValue.artist,
    0,
  );

  const artistPayoutsSum = payoutsData.reduce((previousValue: number, currentValue) => {
    if (!currentValue.approvedAmount || currentValue.status !== 'completed') return 0;

    return previousValue + currentValue.approvedAmount;
  }, 0);

  return artistRevenueSum - artistPayoutsSum;
};
